import { useMemo, useEffect, useState } from 'react'
import { createArrowPath, getArrowPosition } from '../CameraTrajectoryOverlay'
import { useAppStore } from '@store/appStore'
import { OUTSIDE_FRAME_ZONE } from '@/constants/outsideFrameZone'
import { calculateFrameSequenceTiming, getAnimationConfig } from '@/config/animationConfig'
import { getDoubleClickAnimationService } from '@/services/frameAnimationService'

/**
 * 🧭 NAVIGATION HINT OVERLAY
 * 
 * Completely independent hint controller - ALWAYS VISIBLE
 * - Never hidden by other overlays
 * - Never conditional on store state
 * - Renders in its own fixed container
 * - Handles all hint interactions
 */

/**
 * Helper function to find candidates within a specific perpendicular alignment tolerance
 */
function findCandidatesInZone(
  direction: 'north' | 'south' | 'east' | 'west',
  currentFrame: { frameIndex: number; worldX: number; worldY: number },
  frameWorldPositions: Array<{ frameIndex: number; worldX: number; worldY: number }>,
  halfWidth: number,
  halfHeight: number,
  minDist: number,
  maxDist: number,
  perpAlign: number
): Array<{ frameIndex: number; distance: number; dx: number; dy: number }> {
  const candidates: Array<{
    frameIndex: number
    distance: number
    dx: number
    dy: number
  }> = []

  // Evaluate all frames
  for (const candidate of frameWorldPositions) {
    if (candidate.frameIndex === currentFrame.frameIndex) continue

    const dx = candidate.worldX - currentFrame.worldX
    const dy = candidate.worldY - currentFrame.worldY

    let isValid = false
    let primaryDistance = 0

    switch (direction) {
      case 'east':
        // Frame must be: x in [minDist%, maxDist%] of width AND y in [-perpAlign%, +perpAlign%] of height
        if (
          dx >= halfWidth * minDist &&
          dx <= halfWidth * maxDist &&
          Math.abs(dy) <= halfHeight * perpAlign
        ) {
          isValid = true
          primaryDistance = dx
        }
        break

      case 'west':
        // Frame must be: x in [-maxDist%, -minDist%] of width AND y in [-perpAlign%, +perpAlign%]
        if (
          dx <= -halfWidth * minDist &&
          dx >= -halfWidth * maxDist &&
          Math.abs(dy) <= halfHeight * perpAlign
        ) {
          isValid = true
          primaryDistance = -dx
        }
        break

      case 'north':
        // Frame must be: y in [-maxDist%, -minDist%] of height AND x in [-perpAlign%, +perpAlign%]
        if (
          dy <= -halfHeight * minDist &&
          dy >= -halfHeight * maxDist &&
          Math.abs(dx) <= halfWidth * perpAlign
        ) {
          isValid = true
          primaryDistance = -dy
        }
        break

      case 'south':
        // Frame must be: y in [minDist%, maxDist%] of height AND x in [-perpAlign%, +perpAlign%]
        if (
          dy >= halfHeight * minDist &&
          dy <= halfHeight * maxDist &&
          Math.abs(dx) <= halfWidth * perpAlign
        ) {
          isValid = true
          primaryDistance = dy
        }
        break
    }

    if (isValid) {
      candidates.push({
        frameIndex: candidate.frameIndex,
        distance: primaryDistance,
        dx,
        dy
      })
    }
  }

  return candidates
}

/**
 * Query frames outside current frame boundary with progressive zone expansion
 * 
 * Returns frames that:
 * 1. Fall within distance band (minDistancePercent to maxDistancePercent in primary direction)
 * 2. Are within ±perpendularAlignmentPercent perpendicular alignment
 * 3. Progressive expansion: tries 0.5 → 1.0 → 1.25 if no candidates found
 * 4. Sorted by distance (nearest first)
 */
function queryOutsideFrameCandidate(
  direction: 'north' | 'south' | 'east' | 'west',
  currentFrameIndex: number | null,
  frameWorldPositions: Array<{ frameIndex: number; worldX: number; worldY: number }>,
  imageDimensions: { imageWidth: number; imageHeight: number; displayWidth: number; displayHeight: number } | null
): { candidateFrames: number[]; nearest: number | null } {
  if (
    currentFrameIndex === null ||
    !imageDimensions ||
    frameWorldPositions.length === 0
  ) {
    return { candidateFrames: [], nearest: null }
  }

  const currentFrame = frameWorldPositions[currentFrameIndex]
  if (!currentFrame) {
    return { candidateFrames: [], nearest: null }
  }

  const halfWidth = imageDimensions.displayWidth / 2
  const halfHeight = imageDimensions.displayHeight / 2
  const minDist = OUTSIDE_FRAME_ZONE.minDistancePercent
  const maxDist = OUTSIDE_FRAME_ZONE.maxDistancePercent

  // Progressive expansion: try 0.5 → 1.0 → 1.25
  const perpendiculalTolerance = [0.5, 1.0, 1.25]
  let candidates: Array<{
    frameIndex: number
    distance: number
    dx: number
    dy: number
  }> = []
  let usedPerpAlign = 0.5

  for (const perpAlign of perpendiculalTolerance) {
    candidates = findCandidatesInZone(
      direction,
      currentFrame,
      frameWorldPositions,
      halfWidth,
      halfHeight,
      minDist,
      maxDist,
      perpAlign
    )

    if (candidates.length > 0) {
      usedPerpAlign = perpAlign
      if (perpAlign > 0.5) {
        console.log(`🔍 [OutsideFrameQuery] Expanded zone to ±${(perpAlign * 100).toFixed(0)}%`)
      }
      break
    }
  }

  // Selection logic with sequential proximity bonus:
  // Prefer frames that are both spatially close AND sequentially near
  // Score = spatial_distance + (sequential_distance * SEQUENCE_WEIGHT)
  let selectedFrame: number | null = null
  
  if (candidates.length === 1) {
    // Single candidate: auto-select
    selectedFrame = candidates[0].frameIndex
  } else if (candidates.length > 1) {
    // Multiple candidates: use distance-weighted scoring
    // Weight sequential proximity to prefer nearby frames in the timeline
    const SEQUENCE_WEIGHT = 10 // Adjust to balance spatial vs sequential preference
    
    const bestCandidate = candidates.reduce((best, curr) => {
      const bestSequentialDist = Math.abs(best.frameIndex - currentFrameIndex)
      const currSequentialDist = Math.abs(curr.frameIndex - currentFrameIndex)
      
      const bestScore = best.distance + (bestSequentialDist * SEQUENCE_WEIGHT)
      const currScore = curr.distance + (currSequentialDist * SEQUENCE_WEIGHT)
      
      return currScore < bestScore ? curr : best
    })
    
    selectedFrame = bestCandidate.frameIndex
  }

  // Debug logging
  console.log(`\n🎯 [OutsideFrameQuery] Direction: ${direction}`)
  console.log(`   Current frame: ${currentFrameIndex} at (${currentFrame.worldX.toFixed(1)}, ${currentFrame.worldY.toFixed(1)})`)
  console.log(`   Frame size: ${imageDimensions.displayWidth} × ${imageDimensions.displayHeight}`)
  console.log(`   Distance band: ${(minDist * 100).toFixed(0)}%-${(maxDist * 100).toFixed(0)}%, Perpendicular: ±${(usedPerpAlign * 100).toFixed(0)}%`)
  console.log(`   Candidates found: ${candidates.length}`)
  candidates.forEach((c, i) => {
    const seqDist = Math.abs(c.frameIndex - currentFrameIndex)
    const score = c.distance + (seqDist * 10) // Show combined score in logs
    console.log(`     ${i + 1}. Frame ${c.frameIndex} at (${(currentFrame.worldX + c.dx).toFixed(1)}, ${(currentFrame.worldY + c.dy).toFixed(1)}), spatial: ${c.distance.toFixed(1)}px, sequential: ${seqDist}, score: ${score.toFixed(1)}`)
  })
  if (selectedFrame !== null) {
    console.log(`   ✅ SELECTED: Frame ${selectedFrame} (${candidates.length === 1 ? 'only candidate' : 'best combined score'})`)
  } else {
    console.log(`   ❌ NO CANDIDATES FOUND`)
  }

  return {
    candidateFrames: candidates.map(c => c.frameIndex),
    nearest: selectedFrame
  }
}

/**
 * Handle arrow button click - find outside-frame candidates, select best one, and animate to it
 * Animation pattern follows CameraTrajectoryOverlay's handleCircleInteraction
 */
function handleArrowClick(direction: 'north' | 'south' | 'east' | 'west' | 'forward' | 'backward') {
  const ENABLE_ANIMATION = false // TODO: Set to true to re-enable sequential frame animation
  
  const appState = useAppStore.getState()
  
  // Only handle N/S/E/W directions
  if (!['north', 'south', 'east', 'west'].includes(direction)) return
  
  const typedDirection = direction as 'north' | 'south' | 'east' | 'west'

  // Query candidates in this direction
  const result = queryOutsideFrameCandidate(
    typedDirection,
    appState.manuallySelectedFrameIndex,
    appState.frameWorldPositions,
    appState.imageDimensions
  )

  // Store all candidates for visual highlighting
  appState.setCandidateFrames(result.candidateFrames)
  appState.setHighlightedZoneDirection(typedDirection)
  
  // Navigate to selected frame if one was found
  if (result.nearest === null) {
    console.log(`🎯 [ArrowNavigate] No candidates found in direction: ${typedDirection}`)
    return
  }

  const currentFrame = appState.manuallySelectedFrameIndex
  const targetFrame = result.nearest

  // If clicking current frame or no current frame, just center it
  if (currentFrame === null || currentFrame === targetFrame) {
    console.log(`🎯 [ArrowNavigate] Direct jump to frame ${targetFrame}`)
    appState.setManuallySelectedFrame(targetFrame)
    appState.centerViewportOnFrame(targetFrame)
    return
  }

  if (ENABLE_ANIMATION) {
    // === ANIMATION PATH (DISABLED) ===
    const timing = calculateFrameSequenceTiming(currentFrame, targetFrame)
    const frameCount = Math.abs(targetFrame - currentFrame)

    console.log(`🎬 [ArrowNavigate] Animation: ${currentFrame} → ${targetFrame} (${frameCount} frames)`)
    console.log(`   Timing: ${timing.frameDuration}ms per frame, ${timing.framesToShow.length} frames shown, ~${timing.totalDuration}ms total`)

    appState.setFrameSequenceAnimating(true)
    appState.setFrameSequenceAnimationTarget(targetFrame)
    appState.setAnimatingFrameIndex(timing.framesToShow[0])
    appState.setAnimationTrigger('DOUBLE_CLICK_ANIMATE')

    const animationService = getDoubleClickAnimationService()
    const config = getAnimationConfig('DOUBLE_CLICK_ANIMATE')

    if (config && timing.framesToShow.length > 0) {
      const dynamicConfig = { ...config, frameDuration: timing.frameDuration }

      const cancelFn = animationService.animateFrameSequence(
        timing.framesToShow,
        targetFrame,
        dynamicConfig,
        {
          onFrameShow: (frameIdx: number) => {
            console.log(`🎬 [ArrowNavigate] Showing frame: ${frameIdx}`)
            const freshState = useAppStore.getState()
            freshState.setAnimatingFrameIndex(frameIdx)
            freshState.centerViewportOnFrame(frameIdx, undefined, undefined, { skipAutoSelect: true })
          },
          onComplete: (finalFrame: number) => {
            console.log(`✅ [ArrowNavigate] Animation complete, selecting frame: ${finalFrame}`)
            const freshState = useAppStore.getState()

            freshState.setAnimatingFrameIndex(null)
            freshState.setAnimationTrigger(null)
            freshState.setFrameSequenceAnimating(false)
            freshState.setFrameSequenceAnimationCancel(null)
            freshState.setFrameSequenceAnimationTarget(null)

            freshState.setCandidateFrames([])
            freshState.setHighlightedZoneDirection(null)

            freshState.setManuallySelectedFrame(finalFrame)
            freshState.centerViewportOnFrame(finalFrame)
          }
        }
      )

      appState.setFrameSequenceAnimationCancel(cancelFn)
    }
  } else {
    // === INSTANT JUMP (CURRENT) ===
    const frameCount = Math.abs(targetFrame - currentFrame)
    console.log(`🎯 [ArrowNavigate] Instant jump: ${currentFrame} → ${targetFrame} (${frameCount} frames apart)`)
    
    // Clear candidate highlighting
    appState.setCandidateFrames([])
    appState.setHighlightedZoneDirection(null)
    
    // Jump to target frame
    appState.setManuallySelectedFrame(targetFrame)
    appState.centerViewportOnFrame(targetFrame)
  }
}

export function NavigationHintOverlay() {
  const isFrameSequenceAnimating = useAppStore((state) => state.isFrameSequenceAnimating)
  const panFailureDirection = useAppStore((state) => state.panFailureDirection)
  const manuallySelectedFrameIndex = useAppStore((state) => state.manuallySelectedFrameIndex)
  const frameWorldPositions = useAppStore((state) => state.frameWorldPositions)
  const imageDimensions = useAppStore((state) => state.imageDimensions)
  const [animatingDirection, setAnimatingDirection] = useState<'north' | 'south' | 'east' | 'west' | null>(null)

  // Check which directions have available frames whenever selection changes
  useEffect(() => {
    if (manuallySelectedFrameIndex === null || !imageDimensions || frameWorldPositions.length === 0) {
      useAppStore.getState().setAvailableDirections(['north', 'south', 'east', 'west'])
      return
    }

    const availableDirs: ('north' | 'south' | 'east' | 'west')[] = []
    const directions = ['north', 'south', 'east', 'west'] as const

    for (const dir of directions) {
      const result = queryOutsideFrameCandidate(
        dir,
        manuallySelectedFrameIndex,
        frameWorldPositions,
        imageDimensions
      )
      if (result.candidateFrames.length > 0) {
        availableDirs.push(dir)
      }
    }

    console.log(`🧭 [NavigationHintOverlay] Available directions for frame ${manuallySelectedFrameIndex}:`, availableDirs)
    useAppStore.getState().setAvailableDirections(availableDirs)
  }, [manuallySelectedFrameIndex, frameWorldPositions, imageDimensions])

  // Watch pan failure direction and animate arrow
  useEffect(() => {
    console.log('🎯 [NavigationHintOverlay] Pan failure direction changed:', panFailureDirection)
    if (panFailureDirection) {
      const dirMap: Record<string, 'north' | 'south' | 'east' | 'west'> = {
        'up': 'north',
        'down': 'south',
        'left': 'west',
        'right': 'east'
      }
      const hintDir = dirMap[panFailureDirection]
      console.log('🎯 [NavigationHintOverlay] Mapped to hint direction:', hintDir, 'animating now')
      if (hintDir) {
        setAnimatingDirection(hintDir)
        setTimeout(() => setAnimatingDirection(null), 3000)
      }
    }
  }, [panFailureDirection])

  // Memoize directions to prevent unnecessary re-renders
  // NOTE: 'forward' and 'backward' (zoom arrows) are disabled and kept for future use
  const directions = useMemo(() => ['north', 'south', 'east', 'west', 'forward', 'backward'] as const, [])
  const enabledDirections = useMemo(() => ['north', 'south', 'east', 'west'] as const, [])
  const availableDirections = useAppStore((state) => state.availableDirections)

  return (
    <div
      data-navigation-hint-overlay
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 9999, // High z-index to always be visible
      }}
    >
      {directions.map((dir) => {
        // Disable forward/backward (zoom) arrows - kept for future implementation
        const isDisabled = !enabledDirections.includes(dir as any)
        // Check if direction has available frames
        const isAvailable = availableDirections.includes(dir as any)
        
        return (
          <button
            key={dir}
            data-hint-direction={dir}
            aria-label={`Navigate ${dir}`}
            onClick={() => handleArrowClick(dir)}
            disabled={isFrameSequenceAnimating || isDisabled}
            style={{
              ...getArrowPosition(dir),
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: isDisabled || !isAvailable ? 'rgba(128, 128, 128, 0.1)' : 'rgba(255, 255, 255, 0.15)',
              border: isDisabled || !isAvailable ? '1.5px solid rgba(128, 128, 128, 0.2)' : '1.5px solid rgba(255, 255, 255, 0.4)',
              backdropFilter: 'blur(10px)',
              opacity: isDisabled || !isAvailable ? 0.3 : 0.6,
              cursor: isFrameSequenceAnimating || isDisabled ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: isDisabled || !isAvailable ? 'none' : 'auto',
              padding: 0,
              transition: 'opacity 0.2s ease',
              position: 'fixed', // Fixed positioning for each button
              zIndex: 9999,
              animation: animatingDirection === dir && isAvailable ? `arrowBounce${dir.charAt(0).toUpperCase() + dir.slice(1)} 1.75s linear 1` : 'none',
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              style={{
                strokeWidth: '2',
                stroke: isDisabled || !isAvailable ? 'rgba(128, 128, 128, 0.4)' : 'rgba(255, 255, 255, 0.8)',
                fill: 'none',
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
              }}
            >
              <path d={createArrowPath(dir)} />
            </svg>
          </button>
        )
      })}
    </div>
  )
}

// CSS animations: 3 cycles with constant speed. 56px in 1s, 28px in 0.5s, 14px in 0.25s (total 1.75s)
const style = document.createElement('style')
style.textContent = `
  @keyframes arrowBounceNorth {
    0% { transform: translateX(-50%) translateY(0); }
    28.57% { transform: translateX(-50%) translateY(-56px); }
    57.14% { transform: translateX(-50%) translateY(0); }
    71.43% { transform: translateX(-50%) translateY(-28px); }
    85.71% { transform: translateX(-50%) translateY(0); }
    92.86% { transform: translateX(-50%) translateY(-14px); }
    100% { transform: translateX(-50%) translateY(0); }
  }
  @keyframes arrowBounceSouth {
    0% { transform: translateX(-50%) translateY(0); }
    28.57% { transform: translateX(-50%) translateY(56px); }
    57.14% { transform: translateX(-50%) translateY(0); }
    71.43% { transform: translateX(-50%) translateY(28px); }
    85.71% { transform: translateX(-50%) translateY(0); }
    92.86% { transform: translateX(-50%) translateY(14px); }
    100% { transform: translateX(-50%) translateY(0); }
  }
  @keyframes arrowBounceEast {
    0% { transform: translateY(-50%) translateX(0); }
    28.57% { transform: translateY(-50%) translateX(56px); }
    57.14% { transform: translateY(-50%) translateX(0); }
    71.43% { transform: translateY(-50%) translateX(28px); }
    85.71% { transform: translateY(-50%) translateX(0); }
    92.86% { transform: translateY(-50%) translateX(14px); }
    100% { transform: translateY(-50%) translateX(0); }
  }
  @keyframes arrowBounceWest {
    0% { transform: translateY(-50%) translateX(0); }
    28.57% { transform: translateY(-50%) translateX(-56px); }
    57.14% { transform: translateY(-50%) translateX(0); }
    71.43% { transform: translateY(-50%) translateX(-28px); }
    85.71% { transform: translateY(-50%) translateX(0); }
    92.86% { transform: translateY(-50%) translateX(-14px); }
    100% { transform: translateY(-50%) translateX(0); }
  }
`
if (!document.head.querySelector('[data-arrow-bounce-style]')) {
  style.setAttribute('data-arrow-bounce-style', 'true')
  document.head.appendChild(style)
}
